import React, { useEffect, useRef, useState } from "react";
import { buildGraphOption } from "../graph/buildGraphOption";

/**
 * Interactive chart for a Copilot SQL result (bar for metrics/groups,
 * network for true relationships).
 *
 * ECharts is loaded with a dynamic `import()` so it never touches the main
 * bundle. On failure we call `onUnavailable` so the parent falls back to the
 * always-present table.
 */
const RenderGraph = ({ graph, onUnavailable }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [height, setHeight] = useState(320);

  useEffect(() => {
    let disposed = false;
    let resizeObserver;

    const option = buildGraphOption(graph);
    if (!option) {
      onUnavailable?.();
      return undefined;
    }

    const chartHeight = Number(option.__height) || 320;
    setHeight(chartHeight);
    setReady(false);

    // Strip helper keys before handing the option to ECharts.
    const { __height, __chartKind, ...echartsOption } = option;

    import("echarts")
      .then((echartsModule) => {
        if (disposed || !containerRef.current) return;
        const echarts = echartsModule.default || echartsModule;
        if (chartRef.current) {
          chartRef.current.dispose();
          chartRef.current = null;
        }
        const chart = echarts.init(containerRef.current, null, {
          renderer: "canvas",
        });
        chart.setOption(echartsOption);
        chartRef.current = chart;
        setReady(true);

        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => {
            if (chartRef.current) chartRef.current.resize();
          });
          resizeObserver.observe(containerRef.current);
        }
      })
      .catch(() => {
        if (!disposed) onUnavailable?.();
      });

    return () => {
      disposed = true;
      if (resizeObserver) resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [graph, onUnavailable]);

  const ariaLabel =
    graph?.layout === "metric" || graph?.layout === "grouped"
      ? "Bar chart"
      : "Relationship graph";

  return (
    <div className="relative mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div
        ref={containerRef}
        className="w-full"
        style={{ height }}
        role="img"
        aria-label={ariaLabel}
      />
      {!ready ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] text-slate-400">
          Preparing chart&hellip;
        </div>
      ) : null}
    </div>
  );
};

export default RenderGraph;
